import React, { useCallback, useContext, useEffect } from 'react';
import { useSelector } from '@xstate/react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Card,
  Column,
  PageHeader,
  Row,
  Stepper,
} from '@chargify/custom-ui-components';
import { useDeviceInfo } from '@chargify/device-provider';

import { subscriptionsSelectors } from '../../machines';
import { addConfigurationSelectors } from '../../machines/addOnsConfiguration';
import { GlobalStateContext } from '../../contexts';
import {
  useAddonsConfigurationService,
  useComponentSettings,
} from '../../hooks';
import { ComponentAllocationsContainer as ComponentsAllocationContainer } from '../../containers/ComponentsAllocationContainer';

const ComponentsAllocation = () => {
  const { t } = useTranslation(['subscriptions-manager', 'payment-profile']);
  const navigate = useNavigate();

  const { subscriptionsService } = useContext(GlobalStateContext);
  const { subscription: subscriptionConfig } = useComponentSettings();

  const { subscriptionId, catalogId } = useParams();
  const addOnsConfigurationService = useAddonsConfigurationService(
    Number(catalogId)
  );

  const { subscription } = useSelector(
    subscriptionsService,
    useCallback(
      subscriptionsSelectors.getSubscriptionById(
        Number(catalogId),
        Number(subscriptionId)
      ),
      []
    )
  );
  const {
    product: { name },
  } = subscription;

  const { device } = useDeviceInfo();
  const isOnMobileDevice = ['tablet', 'mobile'].includes(device);

  const onFinishConfiguration = useCallback(() => {
    if (subscriptionConfig.details === 'page') {
      navigate(`/subscription/${catalogId}/${subscriptionId}`);
    } else {
      navigate('/');
    }
  }, [subscriptionConfig.details]);

  useEffect(() => {
    addOnsConfigurationService.send('INITIALIZE', {
      subscriptionId,
    });
  }, []);

  const allocations = useSelector(
    addOnsConfigurationService,
    addConfigurationSelectors.getAllocations
  );

  const {
    configureAllocationsStep,
    previewAllocationsStep,
    previewAllocationsLoading,
    updateAllocationsLoading,
  } = useSelector(
    addOnsConfigurationService,
    addConfigurationSelectors.getAddonsState
  );

  const getCurrentStep = () => (previewAllocationsStep ? 1 : 0);

  return (
    <>
      <PageHeader
        title={`${name}:`}
        subtitle={t('components_allocations.addons_configuration', {
          ns: 'subscriptions-manager',
        })}
        size={isOnMobileDevice ? 'small' : 'medium'}
      />
      <Card>
        {isOnMobileDevice && (
          <Card.Header>
            <Stepper
              steps={[
                t('components_allocations.modal_edit_addons'),
                t('components_allocations.modal_preview_charges'),
              ]}
              currentStep={getCurrentStep()}
              variant="vertical"
            />
          </Card.Header>
        )}
        <Card.Body>
          <Row rowGap={16}>
            {!isOnMobileDevice && (
              <Column span={6}>
                <Stepper
                  steps={[
                    t('components_allocations.modal_edit_addons'),
                    t('components_allocations.modal_preview_charges'),
                  ]}
                  currentStep={getCurrentStep()}
                  variant="vertical"
                />
              </Column>
            )}
            <Column span={isOnMobileDevice ? 24 : 18}>
              <ComponentsAllocationContainer
                subscriptionId={Number(subscriptionId)}
                addOnsConfigurationService={addOnsConfigurationService}
                onClose={onFinishConfiguration}
              />
            </Column>
          </Row>
        </Card.Body>
        <Card.Footer>
          <Button.Group>
            <Button
              label={t(
                previewAllocationsStep
                  ? 'components_allocations.confirm_changes'
                  : 'components_allocations.go_to_preview',
                {
                  ns: 'subscriptions-manager',
                }
              )}
              disabled={
                previewAllocationsLoading ||
                updateAllocationsLoading ||
                allocations.length === 0
              }
              isLoading={previewAllocationsLoading || updateAllocationsLoading}
              loadingText={t('processing', { ns: 'common' })}
              onClick={() => {
                if (configureAllocationsStep) {
                  addOnsConfigurationService.send('PREVIEW_ALLOCATIONS', {
                    allocations,
                  });
                } else {
                  addOnsConfigurationService.send('UPDATE_ALLOCATIONS', {
                    allocations,
                  });
                }
              }}
            />
            <Button
              color="secondary"
              label={t('cancel', { ns: 'common' })}
              onClick={onFinishConfiguration}
            />
          </Button.Group>
        </Card.Footer>
      </Card>
    </>
  );
};

export default ComponentsAllocation;
